import { useEffect, useRef, useState } from 'react';
import { folderLabel } from 'zitimail-shared-types';
import type {
  Folder,
  MailAddress,
  MailRule,
  MailRuleAction,
  MailRuleCondition,
  MailRuleField,
  MailRuleOp,
} from 'zitimail-shared-types';

import { mail } from '../api/mail';

/** The server's limits, so the buttons stop where it would refuse. */
const MAX_CONDITIONS = 10;
const MAX_ACTIONS = 6;

const NEW_RULE: MailRule = {
  name: '',
  enabled: true,
  match: 'all',
  conditions: [{ field: 'from', op: 'contains', value: '' }],
  actions: [{ type: 'move', folder: '' }],
  stop: true,
};

/** A rule in the editor, with a key that survives reordering. */
export interface EditedRule {
  readonly key: number;
  readonly rule: MailRule;
  /** Scrolled to and focused when it first appears. */
  readonly focus?: 'name' | 'folder';
}

let lastKey = 0;
export const edited = (rule: MailRule, focus?: EditedRule['focus']): EditedRule =>
  focus ? { key: ++lastKey, rule, focus } : { key: ++lastKey, rule };

/** "Make a rule…" from a message: mail from this sender goes to a folder. */
export function ruleFromSender(sender: MailAddress): MailRule {
  return {
    name: `Mail from ${sender.name?.trim() || sender.address}`,
    enabled: true,
    match: 'all',
    conditions: [{ field: 'from', op: 'is', value: sender.address }],
    actions: [{ type: 'move', folder: '' }],
    stop: true,
  };
}

const FIELDS: ReadonlyArray<[MailRuleField, string]> = [
  ['from', 'From'],
  ['to', 'To'],
  ['cc', 'Cc'],
  ['recipient', 'To or Cc'],
  ['subject', 'Subject'],
  ['body', 'Message text'],
  ['header', 'A header…'],
  ['size', 'Size'],
  ['attachment', 'Attachment'],
];

const OP_LABEL: Record<MailRuleOp, string> = {
  contains: 'contains',
  not_contains: "doesn't contain",
  is: 'is',
  starts: 'starts with',
  ends: 'ends with',
  over: 'is over',
  under: 'is under',
  present: 'has an attachment',
  absent: 'has no attachment',
};

const TEXT_OPS: readonly MailRuleOp[] = ['contains', 'not_contains', 'is', 'starts', 'ends'];

/** What each field can be compared with — the same rules the server applies. */
const OPS: Record<MailRuleField, readonly MailRuleOp[]> = {
  from: TEXT_OPS,
  to: TEXT_OPS,
  cc: TEXT_OPS,
  recipient: TEXT_OPS,
  subject: TEXT_OPS,
  header: TEXT_OPS,
  body: ['contains', 'not_contains'],
  size: ['over', 'under'],
  attachment: ['present', 'absent'],
};

const ACTIONS: ReadonlyArray<[MailRuleAction['type'], string]> = [
  ['move', 'Move to folder'],
  ['copy', 'Copy to folder'],
  ['read', 'Mark as read'],
  ['flag', 'Flag'],
  ['forward', 'Forward to'],
  ['delete', 'Delete'],
];

const isText = (field: MailRuleField) => field !== 'size' && field !== 'attachment';

/** A condition moved to another field: keep what still makes sense. */
function withField(c: MailRuleCondition, field: MailRuleField): MailRuleCondition {
  const ops = OPS[field];
  const op = ops.includes(c.op) ? c.op : ops[0]!;
  if (field === 'attachment') {
    return { field, op };
  }
  const value = isText(field) && isText(c.field) ? (c.value ?? '') : '';
  return field === 'header' ? { field, op, header: c.header ?? '', value } : { field, op, value };
}

function withType(a: MailRuleAction, type: MailRuleAction['type']): MailRuleAction {
  const folder = 'folder' in a ? a.folder : '';
  switch (type) {
    case 'move':
    case 'copy':
      return { type, folder };
    case 'forward':
      return { type, to: '' };
    default:
      return { type };
  }
}

const replace = <T,>(list: readonly T[], i: number, item: T): T[] =>
  list.map((x, j) => (i === j ? item : x));
const without = <T,>(list: readonly T[], i: number): T[] => list.filter((_, j) => j !== i);

/**
 * Mail rules. They run on the server (Sieve) on every new message, in order,
 * with no app open; "Run now" applies one to mail already in a folder.
 */
export function RulesPanel({
  rules,
  folders,
  onChange,
}: {
  rules: readonly EditedRule[];
  folders: readonly Folder[];
  onChange: (next: EditedRule[]) => void;
}): JSX.Element {
  const move = (i: number, by: -1 | 1) => {
    const next = [...rules];
    [next[i], next[i + by]] = [next[i + by]!, next[i]!];
    onChange(next);
  };
  return (
    <section className="panel">
      <h3>Rules</h3>
      <p className="muted">
        Rules sort new mail as it arrives, even when webmail is closed. They run in order, top
        first.
      </p>
      {rules.length === 0 && <p className="muted">No rules.</p>}
      <datalist id="rule-folders">
        {folders.map((f) => (
          <option key={f.path} value={f.path}>
            {folderLabel(f, folders)}
          </option>
        ))}
      </datalist>
      {rules.map((r, i) => (
        <RuleCard
          key={r.key}
          rule={r.rule}
          focus={r.focus}
          folders={folders}
          first={i === 0}
          last={i === rules.length - 1}
          onChange={(rule) => onChange(replace(rules, i, { ...r, rule }))}
          onMove={(by) => move(i, by)}
          onRemove={() => onChange(without(rules, i))}
        />
      ))}
      <button
        type="button"
        className="btn ghost"
        style={{ width: 'auto', alignSelf: 'flex-start' }}
        onClick={() => onChange([...rules, edited({ ...NEW_RULE }, 'name')])}
      >
        Add rule
      </button>
    </section>
  );
}

function RuleCard({
  rule,
  focus,
  folders,
  first,
  last,
  onChange,
  onMove,
  onRemove,
}: {
  rule: MailRule;
  focus: EditedRule['focus'];
  folders: readonly Folder[];
  first: boolean;
  last: boolean;
  onChange: (rule: MailRule) => void;
  onMove: (by: -1 | 1) => void;
  onRemove: () => void;
}): JSX.Element {
  const card = useRef<HTMLDivElement>(null);
  const set = (change: Partial<MailRule>) => onChange({ ...rule, ...change });

  // A rule just added, or made from a message, is where the user is going.
  useEffect(() => {
    if (!focus || !card.current) {
      return;
    }
    card.current.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
    const target = card.current.querySelector<HTMLInputElement>(
      focus === 'folder' ? '[data-folder]' : '[data-name]',
    );
    target?.focus({ preventScroll: true });
    // Only on first appearance.
  }, []);

  return (
    <div className="rule-card" ref={card} role="group" aria-label={rule.name || 'New rule'}>
      <div className="rule">
        <label className="check rule-on">
          <input
            type="checkbox"
            checked={rule.enabled}
            onChange={(e) => set({ enabled: e.target.checked })}
            aria-label="Enabled"
          />
          On
        </label>
        <input
          data-name=""
          value={rule.name}
          onChange={(e) => set({ name: e.target.value })}
          placeholder="Rule name"
          aria-label="Rule name"
          maxLength={100}
        />
      </div>

      <div className="rule-line">
        When
        <select
          value={rule.match}
          onChange={(e) => set({ match: e.target.value as MailRule['match'] })}
          aria-label="Match"
        >
          <option value="all">all</option>
          <option value="any">any</option>
        </select>
        of these are true
      </div>
      {rule.conditions.map((c, i) => (
        <ConditionRow
          key={i}
          condition={c}
          onChange={(next) => set({ conditions: replace(rule.conditions, i, next) })}
          onRemove={
            rule.conditions.length > 1
              ? () => set({ conditions: without(rule.conditions, i) })
              : undefined
          }
        />
      ))}
      {rule.conditions.length < MAX_CONDITIONS && (
        <button
          type="button"
          className="text-btn rule-add"
          onClick={() =>
            set({ conditions: [...rule.conditions, { field: 'from', op: 'contains', value: '' }] })
          }
        >
          Add condition
        </button>
      )}

      <div className="rule-line">Do this</div>
      {rule.actions.map((a, i) => (
        <div className="rule" key={i}>
          <select
            value={a.type}
            onChange={(e) =>
              set({
                actions: replace(rule.actions, i, withType(a, e.target.value as typeof a.type)),
              })
            }
            aria-label="Action"
          >
            {ACTIONS.map(([type, label]) => (
              <option key={type} value={type}>
                {label}
              </option>
            ))}
          </select>
          {(a.type === 'move' || a.type === 'copy') && (
            <input
              data-folder=""
              list="rule-folders"
              value={a.folder}
              onChange={(e) =>
                set({ actions: replace(rule.actions, i, { ...a, folder: e.target.value }) })
              }
              placeholder="Folder"
              aria-label="Folder"
              maxLength={200}
            />
          )}
          {a.type === 'forward' && (
            <input
              type="email"
              value={a.to}
              onChange={(e) =>
                set({ actions: replace(rule.actions, i, { ...a, to: e.target.value.trim() }) })
              }
              placeholder="name@example.com"
              aria-label="Forward to address"
            />
          )}
          {rule.actions.length > 1 && (
            <button
              type="button"
              className="text-btn"
              aria-label="Remove action"
              onClick={() => set({ actions: without(rule.actions, i) })}
            >
              Remove
            </button>
          )}
        </div>
      ))}
      {rule.actions.length < MAX_ACTIONS && (
        <button
          type="button"
          className="text-btn rule-add"
          onClick={() => set({ actions: [...rule.actions, { type: 'move', folder: '' }] })}
        >
          Add action
        </button>
      )}

      <label className="check">
        <input
          type="checkbox"
          checked={rule.stop}
          onChange={(e) => set({ stop: e.target.checked })}
        />
        Stop processing more rules
      </label>

      <div className="rule-foot">
        <RunNow rule={rule} folders={folders} />
        <span className="rule-order">
          <button type="button" className="text-btn" disabled={first} onClick={() => onMove(-1)}>
            Move up
          </button>
          <button type="button" className="text-btn" disabled={last} onClick={() => onMove(1)}>
            Move down
          </button>
          <button type="button" className="text-btn" onClick={onRemove}>
            Remove rule
          </button>
        </span>
      </div>
    </div>
  );
}

function ConditionRow({
  condition: c,
  onChange,
  onRemove,
}: {
  condition: MailRuleCondition;
  onChange: (next: MailRuleCondition) => void;
  onRemove: (() => void) | undefined;
}): JSX.Element {
  return (
    <div className="rule">
      <select
        value={c.field}
        onChange={(e) => onChange(withField(c, e.target.value as MailRuleField))}
        aria-label="Field"
      >
        {FIELDS.map(([field, label]) => (
          <option key={field} value={field}>
            {label}
          </option>
        ))}
      </select>
      {c.field === 'header' && (
        <input
          value={c.header ?? ''}
          onChange={(e) => onChange({ ...c, header: e.target.value.trim() })}
          placeholder="Header, e.g. List-Id"
          aria-label="Header name"
          maxLength={60}
        />
      )}
      <select
        value={c.op}
        onChange={(e) => onChange({ ...c, op: e.target.value as MailRuleOp })}
        aria-label="Test"
      >
        {OPS[c.field].map((op) => (
          <option key={op} value={op}>
            {OP_LABEL[op]}
          </option>
        ))}
      </select>
      {c.field === 'size' ? (
        <SizeInput kb={c.value ?? ''} onChange={(value) => onChange({ ...c, value })} />
      ) : (
        c.field !== 'attachment' && (
          <input
            value={c.value ?? ''}
            onChange={(e) => onChange({ ...c, value: e.target.value })}
            placeholder="Text"
            aria-label="Value"
            maxLength={300}
          />
        )
      )}
      {onRemove && (
        <button type="button" className="text-btn" aria-label="Remove condition" onClick={onRemove}>
          Remove
        </button>
      )}
    </div>
  );
}

const FACTOR = { KB: 1, MB: 1024 } as const;
type Unit = keyof typeof FACTOR;

const toKb = (text: string, unit: Unit) => {
  const n = Number(text);
  return text.trim() === '' || !Number.isFinite(n) ? '' : String(Math.round(n * FACTOR[unit]));
};

/** A size in KB or MB; the rule always stores KB. */
function SizeInput({ kb, onChange }: { kb: string; onChange: (kb: string) => void }): JSX.Element {
  const whole = Number(kb);
  const [unit, setUnit] = useState<Unit>(whole > 0 && whole % 1024 === 0 ? 'MB' : 'KB');
  const [text, setText] = useState(kb === '' ? '' : String(whole / FACTOR[unit]));
  // What was typed, unless the rule changed underneath it.
  const shown = toKb(text, unit) === kb ? text : kb === '' ? '' : String(whole / FACTOR[unit]);
  return (
    <>
      <input
        type="number"
        min={0}
        step="any"
        value={shown}
        onChange={(e) => {
          setText(e.target.value);
          onChange(toKb(e.target.value, unit));
        }}
        aria-label="Size"
      />
      <select
        value={unit}
        onChange={(e) => {
          const next = e.target.value as Unit;
          setUnit(next);
          setText(shown);
          onChange(toKb(shown, next));
        }}
        aria-label="Unit"
      >
        <option value="KB">KB</option>
        <option value="MB">MB</option>
      </select>
    </>
  );
}

/** Applies the rule as it stands — saved or not — to mail already in a folder. */
function RunNow({ rule, folders }: { rule: MailRule; folders: readonly Folder[] }): JSX.Element {
  const inbox = folders.find((f) => f.specialUse === '\\Inbox')?.path ?? 'INBOX';
  const [folder, setFolder] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);
  const chosen = folder ?? inbox;
  const name = folders.find((f) => f.path === chosen)?.name ?? chosen;
  const options = folders.some((f) => f.path === chosen)
    ? folders
    : [{ path: chosen, name } as Folder, ...folders];

  const run = () => {
    setRunning(true);
    setResult(null);
    mail
      .runRule({ rule, folder: chosen })
      .then((r) => {
        const text = [
          r.matched === 0
            ? `No messages in ${name} matched.`
            : `Applied to ${r.matched.toLocaleString()} message${r.matched === 1 ? '' : 's'} in ${name}.`,
        ];
        if (r.matched > 0 && rule.actions.some((a) => a.type === 'delete')) {
          text.push('Deleted messages went to Trash.');
        }
        if (r.skipped.includes('forward')) {
          text.push('Forwarding only applies to new mail.');
        }
        if (r.limited) {
          text.push('Only the newest 5,000 messages were checked.');
        }
        setResult({ ok: true, text: text.join(' ') });
      })
      .catch((e: unknown) =>
        setResult({ ok: false, text: e instanceof Error ? e.message : 'The rule did not run.' }),
      )
      .finally(() => setRunning(false));
  };

  return (
    <div className="rule-run">
      <button
        type="button"
        className="btn ghost"
        style={{ width: 'auto' }}
        disabled={running}
        onClick={run}
      >
        {running ? 'Running…' : 'Run now'}
      </button>
      on
      <select
        value={chosen}
        onChange={(e) => setFolder(e.target.value)}
        aria-label="Folder to run on"
      >
        {options.map((f) => (
          <option key={f.path} value={f.path}>
            {f.specialUse === '\\Inbox' || f.path.toUpperCase() === 'INBOX'
              ? 'Inbox'
              : folderLabel(f, options)}
          </option>
        ))}
      </select>
      {result && (
        <span className={result.ok ? 'ok' : 'error'} role="status">
          {result.text}
        </span>
      )}
    </div>
  );
}
